import type { ApiErrorBody, ErrorCode } from '@kindred/types';

/**
 * Cookie-based API client. The browser holds NO tokens — the httpOnly session
 * cookies are sent automatically via `credentials: 'include'`. Normalises the
 * uniform error envelope into a thrown `ApiClientError`, and transparently
 * retries once after a 401 by calling /auth/refresh (rotating refresh, spec-4).
 */
const BASE = '/api/v1';
const CSRF_COOKIE = 'k_csrf';

export class ApiClientError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;
  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details;
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1];
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Extra headers (e.g. Idempotency-Key for ingest-style calls). */
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
  /** Internal: prevents infinite refresh loops. */
  _retried?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('http') ? path : `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function refreshSession(): Promise<boolean> {
  const csrf = readCookie(CSRF_COOKIE);
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'x-csrf-token': csrf } : {},
  });
  return res.ok;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { ...opts.headers };

  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  // Double-submit CSRF token on mutations.
  if (method !== 'GET') {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method,
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // Transparent single refresh-and-retry on 401.
  if (res.status === 401 && !opts._retried && !path.startsWith('/auth/')) {
    const ok = await refreshSession();
    if (ok) return apiRequest<T>(path, { ...opts, _retried: true });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const body = (json ?? {
      error: { code: 'internal', message: res.statusText },
    }) as ApiErrorBody;
    throw new ApiClientError(res.status, body);
  }
  return json as T;
}

/** Ergonomic verb helpers. */
export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    apiRequest<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    apiRequest<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    apiRequest<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...opts, method: 'DELETE' }),

  /**
   * Multipart file upload. Does NOT set Content-Type — the browser sets it with
   * the correct boundary. Inherits cookie-session, CSRF, and 401-retry handling.
   */
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const headers: Record<string, string> = {};
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers['x-csrf-token'] = csrf;

    const doFetch = () =>
      fetch(`${BASE}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

    let res = await doFetch();

    if (res.status === 401) {
      const ok = await refreshSession();
      if (ok) res = await doFetch();
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const json = text ? JSON.parse(text) : undefined;

    if (!res.ok) {
      const body = (json ?? {
        error: { code: 'internal', message: res.statusText },
      }) as ApiErrorBody;
      throw new ApiClientError(res.status, body);
    }
    return json as T;
  },
};
